/**
 * In-memory fakes for the P14-05/06 seams.
 *
 * What each one is for:
 *
 *   {@link FakeRetentionPort} runs the real arithmetic — `retentionSweepDue` — over whatever
 *   policies were seeded, so the dispatcher's sweep can be exercised end to end without a
 *   database. A workspace set to "keep until I delete them" produces no task at all, which is
 *   the behaviour a naive sweep gets wrong by producing a task that deletes nothing.
 *
 *   {@link FakeAuditEntryPort} **validates the action code against the registry**. An audit
 *   row with an unregistered code is refused here rather than written, because the Audit
 *   log's filter is built from that registry and a row outside it is a row nobody can find.
 *   That is the fake's whole point: a consumer wiring up a new mutating route discovers the
 *   missing registry entry while writing the route, not after shipping it.
 *
 *   {@link FakePersonDataRequestPort} exposes `complete()` so a suite can finish a request at
 *   a chosen instant and watch AC-34's verdict flip from `pending` to `completed` — and, by
 *   advancing the clock instead, to `overdue`.
 */
import { auditEntryId as auditEntryIdCodec, consentLineId as consentLineIdCodec, personDataRequestId as personDataRequestIdCodec, retentionPolicyId as retentionPolicyIdCodec, } from "../../id/codec.js";
import { auditActionOf } from "../../compliance/audit-action.js";
import { RETENTION_DEFAULT_DAY } from "../../compliance/governance.js";
import { judgePersonDataRequest, retentionSweepDue } from "../../compliance/retention.js";
const RETENTION_CLASS = Object.freeze([
    "recording",
    "transcript",
    "order",
    "survey_result",
    "caller_detail",
    "audit",
]);
function scopeKey(scope) {
    return `${scope.accountId}/${scope.workspaceId}`;
}
export class FakeCompliancePort {
    #deps;
    #consent = new Map();
    #operatorAccess = new Map();
    constructor(deps) {
        this.#deps = deps;
    }
    listConsentLines(scope) {
        return Promise.resolve(this.#consent.get(scopeKey(scope)) ?? []);
    }
    putConsentLine(scope, draft) {
        const key = scopeKey(scope);
        const held = this.#consent.get(key) ?? [];
        const existing = held.find((one) => one.medium === draft.medium && one.languageCode === draft.languageCode);
        const made = {
            accountId: scope.accountId,
            consentLineId: existing?.consentLineId ?? consentLineIdCodec.next(this.#deps.clock, this.#deps.random),
            workspaceId: scope.workspaceId,
            medium: draft.medium,
            languageCode: draft.languageCode,
            text: draft.text,
            enabled: draft.enabled,
            updatedAt: new Date(this.#deps.clock.now()),
        };
        this.#consent.set(key, [
            ...held.filter((one) => one.consentLineId !== made.consentLineId),
            made,
        ]);
        return Promise.resolve(made);
    }
    removeConsentLine(scope, consentLineId) {
        const key = scopeKey(scope);
        this.#consent.set(key, (this.#consent.get(key) ?? []).filter((one) => one.consentLineId !== consentLineId));
        return Promise.resolve();
    }
    getOperatorAccess(scope) {
        const found = this.#operatorAccess.get(scopeKey(scope));
        if (found !== undefined)
            return Promise.resolve(found);
        return Promise.reject(new Error(`No operator-access setting seeded for ${scopeKey(scope)}. Call setOperatorAccess ` +
            `first: whether wetalk staff may open a recording is never assumed.`));
    }
    setOperatorAccess(scope, enabled, changedByPersonId) {
        const made = {
            workspaceId: scope.workspaceId,
            enabled,
            changedAt: new Date(this.#deps.clock.now()),
            changedByPersonId,
        };
        this.#operatorAccess.set(scopeKey(scope), made);
        return Promise.resolve(made);
    }
}
export class FakeRetentionPort {
    #deps;
    #policy = new Map();
    #scope = new Map();
    swept = [];
    constructor(deps) {
        this.#deps = deps;
    }
    /** Seeds the platform defaults for one workspace, so `dueWindow` has something to find. */
    seedDefaults(scope, changedByPersonId) {
        const key = scopeKey(scope);
        this.#scope.set(key, scope);
        this.#policy.set(key, RETENTION_CLASS.map((retentionClass) => ({
            accountId: scope.accountId,
            retentionPolicyId: retentionPolicyIdCodec.next(this.#deps.clock, this.#deps.random),
            workspaceId: scope.workspaceId,
            class: retentionClass,
            retainDay: RETENTION_DEFAULT_DAY[retentionClass],
            updatedAt: new Date(this.#deps.clock.now()),
            updatedByPersonId: changedByPersonId,
        })));
    }
    listRetentionPolicies(scope) {
        return Promise.resolve(this.#policy.get(scopeKey(scope)) ?? []);
    }
    putRetentionPolicy(scope, retentionClass, retainDay, changedByPersonId) {
        const key = scopeKey(scope);
        this.#scope.set(key, scope);
        const held = this.#policy.get(key) ?? [];
        const existing = held.find((one) => one.class === retentionClass);
        const made = {
            accountId: scope.accountId,
            retentionPolicyId: existing?.retentionPolicyId ??
                retentionPolicyIdCodec.next(this.#deps.clock, this.#deps.random),
            workspaceId: scope.workspaceId,
            class: retentionClass,
            retainDay,
            updatedAt: new Date(this.#deps.clock.now()),
            updatedByPersonId: changedByPersonId,
        };
        this.#policy.set(key, [...held.filter((one) => one.class !== retentionClass), made]);
        return Promise.resolve(made);
    }
    dueWindow(now, limit) {
        const task = [];
        for (const [key, held] of this.#policy) {
            const scope = this.#scope.get(key);
            if (scope === undefined)
                continue;
            for (const window of retentionSweepDue(held, now)) {
                task.push({
                    workspaceId: scope.workspaceId,
                    class: window.class,
                    retainDay: window.retainDay,
                    deleteBefore: window.deleteBefore,
                });
            }
        }
        return Promise.resolve(task.slice(0, limit));
    }
    sweepClass(task) {
        const made = {
            workspaceId: task.workspaceId,
            class: task.class,
            rowsDeleted: 0,
            blobsDeleted: 0,
            sweptAt: new Date(this.#deps.clock.now()),
        };
        this.swept.push(made);
        return Promise.resolve(made);
    }
}
export class FakePersonDataRequestPort {
    #deps;
    #row = [];
    constructor(deps) {
        this.#deps = deps;
    }
    #track(request, now) {
        return {
            request,
            progress: judgePersonDataRequest(request.requestedAt, request.completedAt, now),
        };
    }
    #now() {
        return new Date(this.#deps.clock.now());
    }
    start(scope, draft) {
        const made = {
            accountId: scope.accountId,
            personDataRequestId: personDataRequestIdCodec.next(this.#deps.clock, this.#deps.random),
            workspaceId: scope.workspaceId,
            kind: draft.kind,
            searchTermHash: `hash:${draft.searchTerm}`,
            requestedByPersonId: draft.requestedByPersonId,
            requestedAt: new Date(this.#deps.clock.now()),
            completedAt: null,
            artefactBlobName: null,
            affectedCount: null,
        };
        this.#row.push(made);
        return Promise.resolve(made);
    }
    /** Finishes one, so the `completed` arm of AC-34's verdict is reachable in a suite. */
    complete(personDataRequestId, affectedCount) {
        const at = this.#row.findIndex((one) => one.personDataRequestId === personDataRequestId);
        const found = this.#row[at];
        if (at < 0 || found === undefined) {
            throw new Error(`No person_data_request ${personDataRequestId} in this fake.`);
        }
        this.#row[at] = { ...found, completedAt: this.#now(), affectedCount };
    }
    get(scope, personDataRequestId) {
        const found = this.#row.find((one) => one.accountId === scope.accountId && one.personDataRequestId === personDataRequestId);
        if (found === undefined) {
            return Promise.reject(new Error(`No person_data_request ${personDataRequestId}.`));
        }
        return Promise.resolve(this.#track(found, this.#now()));
    }
    listRecent(scope) {
        const now = this.#now();
        return Promise.resolve(this.#row
            .filter((one) => one.accountId === scope.accountId && one.workspaceId === scope.workspaceId)
            .map((one) => this.#track(one, now)));
    }
    listDue(now, withinMs) {
        const due = this.#row
            .filter((one) => one.completedAt === null)
            .map((one) => this.#track(one, now))
            .filter((one) => one.progress.state === "overdue" ||
            (one.progress.state === "pending" && one.progress.remainingMs <= withinMs));
        return Promise.resolve(due);
    }
}
export class FakeAuditEntryPort {
    #deps;
    #row = new Map();
    constructor(deps) {
        this.#deps = deps;
    }
    listAuditEntries(scope, query) {
        const held = this.#row.get(scopeKey(scope)) ?? [];
        const matched = held.filter((one) => {
            if (query.eventClass !== null && auditActionOf(one.actionCode).eventClass !== query.eventClass) {
                return false;
            }
            if (query.fromOccurredAt !== null && one.occurredAt < query.fromOccurredAt)
                return false;
            if (query.toOccurredAt !== null && one.occurredAt > query.toOccurredAt)
                return false;
            return true;
        });
        return Promise.resolve({ entry: matched, nextCursor: null, matchedCount: matched.length });
    }
    getAuditEntry(scope, auditEntryId) {
        const found = (this.#row.get(scopeKey(scope)) ?? []).find((one) => one.auditEntryId === auditEntryId);
        if (found === undefined) {
            return Promise.reject(new Error(`No audit_entry ${auditEntryId} in this workspace.`));
        }
        return Promise.resolve(found);
    }
    write(scope, draft) {
        // Raises for an unregistered code. Audit-or-abort: better to refuse the write than to
        // file a row under a heading the Audit log's filter does not offer.
        auditActionOf(draft.actionCode);
        const made = {
            accountId: scope.accountId,
            auditEntryId: auditEntryIdCodec.next(this.#deps.clock, this.#deps.random),
            workspaceId: scope.workspaceId,
            occurredAt: new Date(this.#deps.clock.now()),
            actorKind: draft.actorKind,
            actorPersonId: draft.actorPersonId,
            actorOperatorId: draft.actorOperatorId,
            impersonationGrantId: null,
            actionCode: draft.actionCode,
            targetKind: draft.targetKind,
            targetId: draft.targetId,
            summary: draft.summary,
            detail: draft.detail,
            sourceIpHash: draft.sourceIpHash,
        };
        const key = scopeKey(scope);
        this.#row.set(key, [made, ...(this.#row.get(key) ?? [])]);
        return Promise.resolve(made);
    }
    requestCsv(scope, _query) {
        return Promise.resolve({
            downloadUrl: `https://download.wetalk.example/audit/${scope.workspaceId}.csv`,
            expiresAt: new Date(this.#deps.clock.now() + 900_000),
        });
    }
}
//# sourceMappingURL=fake-governance.js.map